import { zodResolver } from '@hookform/resolvers/zod/dist/zod';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { useForm, UseFormReturn } from 'react-hook-form';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { getCallCenterUpdateSchema, RoleEnum } from '_lib/api/apiTypes.shim';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDetailsAccordion } from '_lib/components/ErrorDetailsAccordion/ErrorDetailsAccordion';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';
import { useIsOverflowing } from '_lib/hooks/useIsOverflowing';

import { extractAllErrors } from '_lib/utils/helpers';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';
import { logDebug } from '_lib/utils/logging';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { AuthUser } from '../auth/auth.types';
import { AuthContext } from '../auth/authContext';
import { NavigationBlocker } from '../NavigationBlocker/NavigationBlocker';
import { RouteErrorBoundary } from '../Routing/RouteErrorBoundary';

import { CallCenterForm, CallCenterFormValues } from './CallCenterForm';

type CallCenterUpdateProps = {
  callCenterId: string;
  authUser: AuthUser | undefined;
  isFetchingFromProps?: boolean;
};

export const CallCenterUpdate = (props: CallCenterUpdateProps) => {
  const { callCenterId, authUser, isFetchingFromProps } = props;

  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>();
  const [allErrors, setAllErrors] = useState<string[]>();

  const { enqueueSnackbar } = useOurSnackbar();

  const scrollTriggerTarget = useRef<HTMLDivElement>(null);
  const { isOverflowing } = useIsOverflowing(scrollTriggerTarget);

  const api = useContext(ApiContext);
  const { callCenterCacheResource } = useContext(AuthContext);

  const callCenterPutMutation = api.callCenters.usePut();

  const callCenterQuery = api.callCenters.useGetById({ id: callCenterId });
  const callCenterData = callCenterQuery?.data;

  const callCenterUpdateFormContext = useForm<Partial<CallCenterFormValues>>({
    context: { authUser },
    resolver: (values, context: { authUser: AuthUser | undefined } | undefined, options) => {
      const schema = getCallCenterUpdateSchema(context?.authUser || { role: RoleEnum.USER });
      const resolver = zodResolver(schema);
      return resolver(values, context, options);
    },
    mode: 'onChange',
    shouldFocusError: true
  });

  useEffect(() => {
    if (callCenterData) {
      callCenterUpdateFormContext.reset(callCenterData);
    }
  }, [callCenterData, callCenterUpdateFormContext]);

  const handleSave = apiSubmitHandler(
    callCenterUpdateFormContext.handleSubmit,
    callCenterUpdateFormContext.setError,
    async (data, { setFormErrors }) => {
      setNonFieldErrors(undefined);
      setAllErrors(undefined);
      try {
        const result = await callCenterPutMutation.put({ id: callCenterId, data });
        if (result.id === (await callCenterCacheResource.get())?.id) {
          await callCenterCacheResource.set(result).catch(() => {});
        }
        callCenterUpdateFormContext.reset(result);
        enqueueSnackbar('Call Center saved');
      } catch (err) {
        if (err instanceof BadRequestError) {
          setNonFieldErrors(err.responseData?.nonFieldErrors);
          setFormErrors(err);
        }
      }
    },
    {
      onInvalid: (errors) => {
        enqueueSnackbar('Validation Error occurred. Please check inputs and try again', { variant: 'error' });
        const allErrors = extractAllErrors(errors);
        setAllErrors(allErrors);
        logDebug(errors, allErrors);
      }
    }
  );

  if (callCenterQuery.error) {
    return <RouteErrorBoundary errorTitle='Error Fetching Call Center' />;
  }

  const isFetchingOrPending = callCenterQuery.isFetching || isFetchingFromProps || callCenterPutMutation.isPending;

  if (!isFetchingOrPending && !callCenterData) {
    return <RouteErrorBoundary errorTitle='Call Center Not Found' />;
  }

  return (
    <FlexBox
      sx={(theme) => ({
        backgroundColor: theme.palette.background.default,
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        position: 'relative'
      })}>
      <NavigationBlocker
        getBlockCondition={() => Object.keys(callCenterUpdateFormContext.formState.dirtyFields).length > 0}
      />
      <BackdropSpinner open={isFetchingOrPending} />
      {!!callCenterData && authUser && !!Object.keys(callCenterUpdateFormContext.getValues()).length && (
        <>
          <Stack padding={2} useFlexGap spacing={2} overflow='auto' ref={scrollTriggerTarget}>
            {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
            {!!allErrors?.length && <ErrorDetailsAccordion errors={allErrors} />}
            <CallCenterForm
              callCenterFormContext={callCenterUpdateFormContext as UseFormReturn<CallCenterFormValues>}
              authUser={authUser}
            />
          </Stack>
          {isOverflowing && <Divider />}
          <FlexBox sx={{ paddingX: 2, paddingY: 1, marginLeft: 'auto' }}>
            <Button onClick={handleSave} text='Save' variant='contained' size='small' sx={{ textTransform: 'none' }} />
          </FlexBox>
        </>
      )}
    </FlexBox>
  );
};
